import { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '../services/notifications';
import type { NotificationTemplate, NotificationLogEntry } from '../types/notifications';

interface UseNotificationTemplatesReturn {
  templates: NotificationTemplate[];
  loading: boolean;
  error: string | null;
}

export function useNotificationTemplates(): UseNotificationTemplatesReturn {
  const [state, setState] = useState<{
    templates: NotificationTemplate[];
    loading: boolean;
    error: string | null;
  }>({
    templates: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await notificationsService.getTemplates();
        if (!cancelled) setState({ templates: Array.isArray(data) ? data : [], loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to fetch templates';
          setState((prev) => ({ ...prev, loading: false, error: message }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

interface UseNotificationHistoryParams {
  limit: number;
  offset: number;
}

interface UseNotificationHistoryReturn {
  items: NotificationLogEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useNotificationHistory(params: UseNotificationHistoryParams): UseNotificationHistoryReturn {
  const [state, setState] = useState<{
    items: NotificationLogEntry[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await notificationsService.getHistory({ limit: params.limit, offset: params.offset });
      setState({
        items: Array.isArray(data.data) ? data.data : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch notification history';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(params.offset / params.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / params.limit));

  return { ...state, page, totalPages, refetch: fetch };
}
